// src/pages/memberProfile/PartnerSingle.jsx
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import i18n from "@/i18n";

import cls from "./PartnerSingle.module.scss";

import GenderMaleIconMini from "@/components/icons/GenderMaleIconMini";
import GenderWomanIconMini from "@/components/icons/GenderWomanIconMini";
import GenderNonBinaryIconMini from "@/components/icons/GenderNonBinaryIconMini";
import GenderTransManIconMini from "@/components/icons/GenderTransManIconMini";
import GenderTransWomanIconMini from "@/components/icons/GenderTransWomanIconMini";
import GenderGenderfluidIconMini from "@/components/icons/GenderGenderfluidIconMini";
import GenderSelfDescribeIconMini from "@/components/icons/GenderSelfDescribeIconMini";

import WhoWeAreIconMini from "@/components/icons/WhoWeAreIconMini";
import PortalIconMini from "@/components/icons/PortalIconMini";

import api from "@/utils/axios";

const GENDER_ICON_MAP = {
  man: GenderMaleIconMini,
  woman: GenderWomanIconMini,
  non_binary: GenderNonBinaryIconMini,
  trans_man: GenderTransManIconMini,
  trans_woman: GenderTransWomanIconMini,
  genderfluid: GenderGenderfluidIconMini,
  self_describe: GenderSelfDescribeIconMini,
  not_select: GenderSelfDescribeIconMini,
};

function getGenderIcon(g) {
  return GENDER_ICON_MAP[g] || GenderSelfDescribeIconMini;
}

const cardVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.35, ease: "easeOut" },
  },
};

const navVariants = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { delay: 0.15, duration: 0.28, ease: "easeOut" },
  },
};

const PartnerSingle = () => {
  // route: /member/my-profile/partners/:partnerId
  const { partnerId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [personal, setPersonal] = useState(null);

  // delete dialog state
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!partnerId) {
      console.warn("No partnerId in params");
      setLoading(false);
      return;
    }

    const fetchPartner = async () => {
      try {
        const res = await api.get(`/v1/member/profile/partners/${partnerId}`);

        setPartner(res.data.partner || null);
        setPersonal(res.data.personal || null);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPartner();
  }, [partnerId]);

  const handleBack = () => {
    navigate("/member/my-profile/partners");
  };

  const handleDeleteClick = () => {
    setShowConfirm(true);
    setDeleteError("");
  };

  const handleCancelDelete = () => {
    if (deleting) return;
    setShowConfirm(false);
    setDeleteError("");
  };

  const handleConfirmDelete = async () => {
    if (!partnerId) return;

    setDeleting(true);
    setDeleteError("");

    try {
      const res = await api.delete(`/v1/member/profile/partners/${partnerId}`);
      const apiMessage = res?.data?.message;

      // Go back to partners list after successful delete
      navigate("/member/my-profile/partners", {
        replace: true,
        state: {
          message:
            apiMessage ||
            i18n.t("partnerSingle.delete.success") ||
            "Partner removed from your profile.",
        },
      });
    } catch (err) {
      console.error(err);
      const apiCode = err?.response?.data?.code;
      const apiMessage = err?.response?.data?.message;

      if (apiCode === "PRIMARY_PARTNER_DELETE_BLOCKED") {
        setDeleteError(
          i18n.t("partnerSingle.delete.errorPrimaryBlocked") || apiMessage,
        );
      } else {
        setDeleteError(
          apiMessage ||
            i18n.t("partnerSingle.delete.errorGeneral") ||
            "Something went wrong while removing your partner. Please try again.",
        );
      }
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <section className={cls.page}>
        <div className={cls.shell}>
          <p style={{ color: "white" }}>Loading…</p>
        </div>
      </section>
    );
  }

  if (!partner || !personal) {
    return (
      <section className={cls.page}>
        <div className={cls.shell}>
          <p style={{ color: "white" }}>Partner not found.</p>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            <span className={cls.backIcon} aria-hidden="true">
              ←
            </span>
            <span>
              {i18n.t("memberMyProfile.backToPartners") || "Back to partners"}
            </span>
          </button>
        </div>
      </section>
    );
  }

  const GenderIcon = getGenderIcon(personal.gender);

  return (
    <section className={cls.page}>
      <div className={cls.shell}>
        {/* Back to Partners */}
        <div className={cls.backRow}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            <span className={cls.backIcon} aria-hidden="true">
              ←
            </span>
            <span>
              {i18n.t("memberMyProfile.backToPartners") || "Back to partners"}
            </span>
          </button>
        </div>

        <Motion.div
          className={cls.card}
          variants={cardVariants}
          initial="hidden"
          animate="show"
        >
          <div className={cls.cardGlow} aria-hidden="true" />

          {/* HEADER: avatar + basic info */}
          <header className={cls.header}>
            <div className={cls.avatarBlock}>
              <div className={cls.avatarShell}>
                <div className={cls.avatarRing} />

                {personal.realImage?.url ? (
                  <img
                    src={personal.realImage.url}
                    alt="Partner Real Image"
                    className={cls.avatarImg}
                  />
                ) : (
                  <div
                    className={cls.avatarImg}
                    style={{
                      background: "#222",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.8rem",
                      color: "#999",
                    }}
                  >
                    No Image
                  </div>
                )}
              </div>

              <div className={cls.genderBadges} aria-hidden="true">
                <span className={cls.genderSolo}>
                  <GenderIcon size={22} />
                </span>
              </div>
            </div>

            <div className={cls.meta}>
              <h1 className={cls.title}>
                {personal.firstName} {personal.lastName}
              </h1>
              <p className={cls.memberType}>
                {personal.gender?.replace("_", " ") || ""}
              </p>
            </div>
          </header>

          {/* NAV BUTTONS ONLY */}
          <Motion.div
            className={cls.navGrid}
            variants={navVariants}
            initial="hidden"
            animate="show"
          >
            {/* Partner Personal Info page */}
            <Link
              to={`/member/my-profile/partners/personal-info/${partnerId}`}
              className={cls.navBtn}
            >
              <div className={cls.navIcon}>
                <WhoWeAreIconMini
                  size={20}
                  title={i18n.t("partnerSingle.actions.personal.title")}
                  data-preserve
                />
              </div>
              <div className={cls.navText}>
                <span className={cls.navTitle}>
                  {i18n.t("partnerSingle.actions.personal.title")}
                </span>
                <span className={cls.navHint}>
                  {i18n.t("partnerSingle.actions.personal.hint")}
                </span>
              </div>
            </Link>

            {/* Partner Lifestyle Info page */}
            <Link
              to={`/member/my-profile/partners/lifestyle-info/${partnerId}`}
              className={cls.navBtn}
            >
              <div className={cls.navIcon}>
                <PortalIconMini
                  size={20}
                  title={i18n.t("partnerSingle.actions.lifestyle.title")}
                  data-preserve
                />
              </div>
              <div className={cls.navText}>
                <span className={cls.navTitle}>
                  {i18n.t("partnerSingle.actions.lifestyle.title")}
                </span>
                <span className={cls.navHint}>
                  {i18n.t("partnerSingle.actions.lifestyle.hint")}
                </span>
              </div>
            </Link>
          </Motion.div>

          {/* DELETE SECTION */}
          <div className={cls.dangerRow}>
            <button
              type="button"
              className={cls.deleteBtn}
              onClick={handleDeleteClick}
            >
              {i18n.t("partnerSingle.delete.button") || "Delete partner"}
            </button>

            {showConfirm && (
              <div className={cls.confirmBox}>
                <p className={cls.confirmTitle}>
                  {i18n.t("partnerSingle.delete.confirmTitle") ||
                    "Remove this partner?"}
                </p>
                <p className={cls.confirmBody}>
                  {i18n.t("partnerSingle.delete.confirmBody") ||
                    "If you remove this partner, they will no longer be linked to your profile."}
                </p>

                {deleteError && <p className={cls.errorText}>{deleteError}</p>}

                <div className={cls.confirmActions}>
                  <button
                    type="button"
                    className={cls.confirmCancelBtn}
                    onClick={handleCancelDelete}
                    disabled={deleting}
                  >
                    {i18n.t("partnerSingle.delete.confirmCancel") || "Cancel"}
                  </button>
                  <button
                    type="button"
                    className={cls.confirmOkBtn}
                    onClick={handleConfirmDelete}
                    disabled={deleting}
                  >
                    {deleting
                      ? i18n.t("partnerSingle.delete.deletingLabel") ||
                        "Deleting…"
                      : i18n.t("partnerSingle.delete.confirmOk") ||
                        "Yes, delete"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </Motion.div>
      </div>
    </section>
  );
};

export default PartnerSingle;
